// ============================================
// AUTH COMPONENT WITH SOCIAL LOGIN + OTP
// Supports Google, Apple, Email/Password with OTP verification
// ============================================

import React, { useState } from 'react';
import { 
  X, Mail, Lock, User, Eye, EyeOff, Phone,
  Loader, AlertCircle, Check, ArrowRight,
  LogIn, UserPlus, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OTPVerification from './OTPVerification';

const Auth = ({ onClose }) => {
  const { login, register, reactivateAccount } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Reactivation state
  const [showReactivate, setShowReactivate] = useState(false);
  const [reactivateData, setReactivateData] = useState({ email: '', password: '' });

  // ============================================
  // OTP STATE - NEW
  // ============================================
  const [showOTP, setShowOTP] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  // ============================================
  // IN-APP BROWSER DETECTION
  // Covers LinkedIn, Instagram, Facebook, Twitter,
  // WhatsApp, Snapchat, TikTok, Android WebView
  // ============================================
  const isInAppBrowser = () => {
    const ua = navigator.userAgent || '';
    return (
      /FBAN|FBAV|Instagram|LinkedIn|Twitter|Line\/|Snapchat|TikTok|Musical|GSA/.test(ua) ||
      (ua.includes('Android') && /wv/.test(ua)) ||
      (ua.includes('iPhone') && !ua.includes('Safari') && !ua.includes('CriOS') && !ua.includes('FxiOS'))
    );
  };

  // ============================================
  // OPEN IN SYSTEM BROWSER
  // Kicks URL out of WebView into Safari / Chrome
  // ============================================
  const openInSystemBrowser = () => {
    const url = 'https://investorq.io';
    const ua = navigator.userAgent || '';
    if (ua.includes('iPhone') || ua.includes('iPad')) {
      window.location.href = `x-safari-https://investorq.io`;
      setTimeout(() => { window.open(url, '_blank'); }, 500);
    } else if (ua.includes('Android')) {
      window.location.href = `intent://investorq.io#Intent;scheme=https;package=com.android.chrome;end`;
      setTimeout(() => { window.open(url, '_blank'); }, 500);
    } else {
      window.open(url, '_blank');
    }
  };

  // ============================================
  // GOOGLE LOGIN (No OTP - Google handles MFA)
  // ============================================
  const handleGoogleLogin = async () => {
    setSocialLoading('google');
    setError('');

    if (isInAppBrowser()) {
      setSocialLoading(null);
      openInSystemBrowser();
      return;
    }
    
    try {
      // Redirect to backend Google OAuth endpoint
      window.location.href = `${API_URL}/auth/google`;
    } catch (err) {
      setError('Failed to initialize Google login');
      setSocialLoading(null);
    }
  };

  // ============================================
  // APPLE LOGIN (No OTP - Apple handles MFA)
  // ============================================
  const handleAppleLogin = async () => {
    setSocialLoading('apple');
    setError('');
    
    try {
      // Redirect to backend Apple OAuth endpoint
      window.location.href = `${API_URL}/auth/apple`;
    } catch (err) {
      setError('Failed to initialize Apple login');
      setSocialLoading(null);
    }
  };

  // ============================================
  // EMAIL/PASSWORD LOGIN - UPDATED WITH OTP
  // ============================================
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if account is deactivated
        if (response.status === 403 && data.isDeactivated) {
          setReactivateData({ email, password });
          setShowReactivate(true);
          setLoading(false);
          return;
        }
        setError(data.error || 'Login failed. Please check your credentials.');
        setLoading(false);
        return;
      }

      // ============================================
      // NEW: Check if OTP is required
      // ============================================
      if (data.requiresOTP) {
        setPendingUserId(data.userId);
        setShowOTP(true);
        setLoading(false);
        return;
      }

      // Direct login (OAuth users shouldn't hit this path)
      if (data.token) {
        localStorage.setItem('investorq_token', data.token);
        localStorage.setItem('investorq_user', JSON.stringify(data.user));
        onClose();
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EMAIL/PASSWORD REGISTER - UPDATED WITH OTP
  // ============================================
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!name || name.trim().length < 2) {
      setError('Please enter your full name');
      setLoading(false);
      return;
    }

    if (!phone || phone.trim().length < 7) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, phone })
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if can reactivate existing account
        if (data.canReactivate) {
          setError(data.error);
          setMode('login');
          setLoading(false);
          return;
        }
        setError(data.error || 'Registration failed. Email may already be in use.');
        setLoading(false);
        return;
      }

      // ============================================
      // NEW: Check if OTP is required after registration
      // ============================================
      if (data.requiresOTP) {
        setPendingUserId(data.userId);
        setShowOTP(true);
        setLoading(false);
        return;
      }

      // Direct login (shouldn't happen with OTP enabled)
      if (data.token) {
        localStorage.setItem('investorq_token', data.token);
        localStorage.setItem('investorq_user', JSON.stringify(data.user));
        onClose();
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // REACTIVATE ACCOUNT - UPDATED WITH OTP
  // ============================================
  const handleReactivate = async () => {
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/users/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: reactivateData.email, 
          password: reactivateData.password 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to reactivate account');
        setLoading(false);
        return;
      }

      // ============================================
      // NEW: Check if OTP is required after reactivation
      // ============================================
      if (data.requiresOTP) {
        setPendingUserId(data.userId);
        setShowReactivate(false);
        setShowOTP(true);
        setLoading(false);
        return;
      }

      // Direct login
      if (data.token) {
        localStorage.setItem('investorq_token', data.token);
        localStorage.setItem('investorq_user', JSON.stringify(data.user));
        onClose();
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // OTP SUCCESS HANDLER - NEW
  // ============================================
  const handleOTPSuccess = (data) => {
    // data contains { token, user } from OTP verification
    localStorage.setItem('investorq_token', data.token);
    localStorage.setItem('investorq_user', JSON.stringify(data.user));
    
    // Reload to update auth state
    window.location.reload();
  };

  // ============================================
  // FORGOT PASSWORD
  // ============================================
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/users/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Password reset link sent to your email!');
      } else {
        setError(data.error || 'Failed to send reset email');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // OTP VERIFICATION SCREEN - NEW
  // ============================================
  if (showOTP) {
    return (
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '0.75rem',
          overflow: 'auto'
        }}>
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            borderRadius: '1rem',
            width: '100%',
            maxWidth: '340px',
            border: '1px solid #475569',
            position: 'relative',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
            margin: 'auto',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'rgba(148, 163, 184, 0.1)',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}
          >
            <X size={20} />
          </button>

          {/* OTP Verification Component */}
          <OTPVerification
            userId={pendingUserId}
            userEmail={email}
            onSuccess={handleOTPSuccess}
            onCancel={() => {
              setShowOTP(false);
              setPendingUserId(null);
            }}
          />
        </div>
      </div>
    );
  }

  // ============================================
  // REACTIVATION MODAL
  // ============================================
  if (showReactivate) {
    return (
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '1rem',
          overflow: 'auto'
        }}>
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            borderRadius: '1rem',
            padding: '2rem',
            width: '100%',
            maxWidth: '420px',
            border: '1px solid #f59e0b',
            position: 'relative',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
            margin: 'auto'
          }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'rgba(148, 163, 184, 0.1)',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>

          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#fbbf24'
          }}>
            <RefreshCw size={24} />
            Reactivate Account
          </h2>
          
          <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            Your account was previously deactivated. Would you like to reactivate it?
          </p>

          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ color: '#fbbf24', margin: 0, fontSize: '0.875rem' }}>
              Account: <strong>{reactivateData.email}</strong>
            </p>
          </div>

          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '0.5rem',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: '#fca5a5',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={() => {
                setShowReactivate(false);
                setError('');
              }}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#334155',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleReactivate}
              disabled={loading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: loading ? '#6b7280' : '#f59e0b',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.875rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? 'Reactivating...' : (
                <>
                  <RefreshCw size={16} />
                  Reactivate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN AUTH MODAL
  // ============================================
  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '1rem',
        overflow: 'auto'
      }}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          borderRadius: '1rem',
          padding: '2rem',
          width: '100%',
          maxWidth: '420px',
          border: '1px solid #475569',
          position: 'relative',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
          margin: 'auto',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(148, 163, 184, 0.1)',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: '0.5rem',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            {mode === 'login' ? <LogIn size={24} color="#60a5fa" /> : mode === 'register' ? <UserPlus size={24} color="#60a5fa" /> : <Mail size={24} color="#60a5fa" />}
            {mode === 'login' ? 'Welcome Back' : mode === 'register' ? 'Create Account' : 'Reset Password'}
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            {mode === 'login' 
              ? 'Sign in to access your portfolio' 
              : mode === 'register' 
                ? 'Start your investing journey'
                : 'Enter your email to reset password'
            }
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#fca5a5',
            fontSize: '0.875rem'
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#86efac',
            fontSize: '0.875rem'
          }}>
            <Check size={16} />
            {success}
          </div>
        )}

        {/* In-App Browser Banner - LinkedIn, Instagram etc */}
        {isInAppBrowser() && (
          <div style={{
            background: 'rgba(37, 99, 235, 0.1)',
            border: '1px solid rgba(37, 99, 235, 0.4)',
            borderRadius: '0.75rem',
            padding: '1rem',
            marginBottom: '1rem',
          }}>
            <p style={{ color: '#93c5fd', fontSize: '0.8rem', margin: '0 0 0.75rem 0', lineHeight: '1.5' }}>
              For the best experience and Google Sign-In, open InvestorQ in your browser.
            </p>
            <button
              onClick={openInSystemBrowser}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <ArrowRight size={16} />
              Open in Safari / Chrome
            </button>
          </div>
        )}

        {/* Social Login Buttons */}
        {mode !== 'forgot' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {/* Google Login */}
              <button
                onClick={handleGoogleLogin}
                disabled={socialLoading === 'google'}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#374151',
                  cursor: socialLoading === 'google' ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  opacity: socialLoading === 'google' ? 0.7 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {socialLoading === 'google' ? (
                  <Loader size={20} className="spin" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                Continue with Google
              </button>

              {/* Apple Login */}
              <button
                onClick={handleAppleLogin}
                disabled={socialLoading === 'apple'}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  background: 'black',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: socialLoading === 'apple' ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  opacity: socialLoading === 'apple' ? 0.7 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {socialLoading === 'apple' ? (
                  <Loader size={20} className="spin" />
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                )}
                Continue with Apple
              </button>
            </div>

            {/* Divider */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ flex: 1, height: '1px', background: '#475569' }}></div>
              <span style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: '#475569' }}></div>
            </div>
          </>
        )}

        {/* Login Form */}
        {mode === 'login' && (
          <form onSubmit={handleEmailLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 2.5rem 0.75rem 2.5rem',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div style={{ textAlign: 'right', marginBottom: '1.5rem' }}>
              <button
                type="button"
                onClick={() => setMode('forgot')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#60a5fa',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: loading ? '#475569' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <Loader size={20} className="spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Register Form */}
        {mode === 'register' && (
          <form onSubmit={handleRegister}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
                Name <span style={{ color: '#f87171' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <User size={18} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Your full name"
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
                Phone Number <span style={{ color: '#f87171' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <Phone size={18} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
                Email <span style={{ color: '#f87171' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
                Password <span style={{ color: '#f87171' }}>*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '0.75rem 2.5rem 0.75rem 2.5rem',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Min 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: loading ? '#475569' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <Loader size={20} className="spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create Account
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#cbd5e1' }}>
                Email
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} color="#94a3b8" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid #475569',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: loading ? '#475569' : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                color: 'white',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <Loader size={20} className="spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Reset Link
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Mode Toggle */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #475569' }}>
          {mode === 'login' ? (
            <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
              Don't have an account?{' '}
              <button
                onClick={() => { setMode('register'); setError(''); setSuccess(''); }}
                style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontWeight: '600' }}
              >
                Sign up
              </button>
            </p>
          ) : mode === 'register' ? (
            <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
              Already have an account?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontWeight: '600' }}
              >
                Sign in
              </button>
            </p>
          ) : (
            <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
              Remember your password?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontWeight: '600' }}
              >
                Back to sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;